import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Box, Button, Typography, Paper, useTheme, Stack, TextField, Select, MenuItem, FormControl, InputLabel, Tabs, Tab, CircularProgress } from '@mui/material';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import JsonView from '@uiw/react-json-view';
import { vscodeTheme } from '@uiw/react-json-view/vscode';
import SendIcon from '@mui/icons-material/Send';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import MonacoEditor from '../../components/MonacoEditor.js';
import HistoryPanel from './HistoryPanel.js';
import KeyValueEditor from './KeyValueEditor.js';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';

// --- Helper Functions ---
const arrayToObj = (arr) => arr.reduce((acc, item) => {
  if (item.key) acc[item.key] = item.value;
  return acc;
}, {});

const buildUrl = (baseUrl, params) => {
  const url = new URL(baseUrl);
  params.forEach(param => {
    if (param.key) url.searchParams.set(param.key, param.value);
  });
  return url.toString();
};

// --- Main Component ---
function ApiClient() {
  const theme = useTheme();
  const { mode } = theme.palette;
  const location = useLocation();

  // Request State
  const [baseUrl, setBaseUrl] = useState('https://jsonplaceholder.typicode.com/posts');
  const [queryParams, setQueryParams] = useState([{ id: 1, key: 'id', value: '1' }]);
  const [method, setMethod] = useState('GET');
  const [headers, setHeaders] = useState([{ id: 1, key: 'Content-Type', value: 'application/json' }]);
  const [body, setBody] = useState('{ "title": "foo", "body": "bar", "userId": 1}');
  const [requestTab, setRequestTab] = useState(0);

  // Response State
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [responseTab, setResponseTab] = useState(0);

  // History State
  const [history, setHistory] = useState([]);

  const bodyEditorRef = useRef(null);

  const finalUrl = useMemo(() => {
    try {
      return buildUrl(baseUrl, queryParams);
    } catch (e) {
      return baseUrl;
    }
  }, [baseUrl, queryParams]);

  // --- Editor Layout Fix ---
  const handleLayout = useCallback(() => {
    // A short delay ensures the editor has time to be visible in the DOM
    setTimeout(() => {
      if (bodyEditorRef.current) {
        bodyEditorRef.current.layout();
      }
    }, 10);
  }, []);

  const handleRequestTabChange = (event, newValue) => {
    setRequestTab(newValue);
    if (newValue === 2) { // If switching to the Body tab
      handleLayout();
    }
  };

  // --- History Management & Initial State ---
  useEffect(() => {
    const savedHistory = localStorage.getItem('apiClientHistory_v2');
    if (savedHistory) setHistory(JSON.parse(savedHistory));
    
    // Check if we navigated here with an initial request body
    if (location.state?.requestBody) {
      setBody(location.state.requestBody);
      setMethod('POST');
      setRequestTab(2); // Switch to body tab automatically
      // Clear state so it doesn't persist on subsequent reloads
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    localStorage.setItem('apiClientHistory_v2', JSON.stringify(history));
  }, [history]);

  // --- API Request Logic ---
  const handleSendRequest = async () => {
    setLoading(true);
    setError(null);
    setResponse(null);

    const newHistoryItem = { id: Date.now(), method, baseUrl, queryParams, headers, body };
    setHistory(prev => [newHistoryItem, ...prev.slice(0, 49)]);

    try {
      const requestConfig = {
        method,
        url: finalUrl,
        headers: arrayToObj(headers),
        data: ['POST', 'PUT', 'PATCH'].includes(method) && body.trim() ? JSON.parse(body) : undefined,
      };
      const startTime = Date.now();
      const result = await axios(requestConfig);
      const endTime = Date.now();
      setResponse({
        data: result.data,
        headers: result.headers,
        status: result.status,
        statusText: result.statusText,
        time: endTime - startTime,
        size: JSON.stringify(result.data).length,
      });
    } catch (err) {
      const errorResponse = err.response || {};
      setError({
        message: err.message,
        status: errorResponse.status,
        statusText: errorResponse.statusText,
        data: errorResponse.data,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectHistory = (id) => {
    const item = history.find(h => h.id === id);
    if (item) {
      setBaseUrl(item.baseUrl);
      setQueryParams(item.queryParams);
      setMethod(item.method);
      setHeaders(item.headers);
      setBody(item.body);
    }
  };

  return (
    <>
      <SEO
        title="REST API Client | Test Endpoints Online | UmbrellaCo"
        description="Free online REST API client to test HTTP requests, view responses, and manage headers/parameters. Similar to Postman, runs entirely in your browser."
        keywords="api client online, rest api tester, http request tool, postman alternative online, test endpoints"
        url="/api-tools/api-client"
        jsonLd={createSoftwareAppSchema(
          'REST API Client',
          'Test REST APIs with a full-featured HTTP client running in your browser.',
          'https://umbrellaco.in/api-tools/api-client',
          ['API Testing', 'HTTP Client', 'Developer Tools']
        )}
      />
      <ToolPageContainer
        title="REST API Client"
        description="Construct and send HTTP requests to test your REST endpoints. Manage query parameters, headers, and request bodies with a built-in JSON editor."
        disablePadding
      >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <PanelGroup direction="horizontal" style={{ height: '100%' }}>
        <Panel defaultSize={20} minSize={15}>
        <HistoryPanel history={history} onSelect={handleSelectHistory} onClear={() => setHistory([])} />
      </Panel>
      <PanelResizeHandle style={{ width: '8px', background: theme.palette.background.paper, borderLeft: `1px solid ${theme.palette.divider}`, borderRight: `1px solid ${theme.palette.divider}` }} />
      <Panel>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 1 }}>
          <Paper elevation={0} sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={1}>
              <FormControl sx={{ minWidth: 120 }} size="small">
                <InputLabel>Method</InputLabel>
                <Select value={method} label="Method" onChange={(e) => setMethod(e.target.value)}>
                  <MenuItem value="GET">GET</MenuItem>
                  <MenuItem value="POST">POST</MenuItem>
                  <MenuItem value="PUT">PUT</MenuItem>
                  <MenuItem value="PATCH">PATCH</MenuItem>
                  <MenuItem value="DELETE">DELETE</MenuItem>
                </Select>
              </FormControl>
              <TextField fullWidth label="URL" value={finalUrl} onChange={(e) => setBaseUrl(e.target.value)} size="small" />
              <Button variant="contained" onClick={handleSendRequest} disabled={loading} startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}>
                Send
              </Button>
            </Stack>
          </Paper>

          <PanelGroup direction="vertical" onLayout={handleLayout}>
            <Panel defaultSize={40} minSize={20}>
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Tabs value={requestTab} onChange={handleRequestTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tab label="Params" />
                  <Tab label="Headers" />
                  <Tab label="Body" />
                </Tabs>
                <Box sx={{ flexGrow: 1, display: requestTab === 0 ? 'block' : 'none' }}><KeyValueEditor data={queryParams} setData={setQueryParams} /></Box>
                <Box sx={{ flexGrow: 1, display: requestTab === 1 ? 'block' : 'none' }}><KeyValueEditor data={headers} setData={setHeaders} /></Box>
                <Box sx={{ flexGrow: 1, display: requestTab === 2 ? 'block' : 'none' }}>
                  <MonacoEditor ref={bodyEditorRef} language="json" value={body} onChange={(value) => setBody(value)} />
                </Box>
              </Box>
            </Panel>
            <PanelResizeHandle style={{ height: '8px', background: theme.palette.background.paper, borderTop: `1px solid ${theme.palette.divider}`, borderBottom: `1px solid ${theme.palette.divider}` }} />
            <Panel defaultSize={60} minSize={20}>
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {loading && <Box sx={{ p: 2 }}><CircularProgress /></Box>}
                {error && <Box sx={{ p: 2 }}><Typography color="error.main">Error: {error.message}</Typography>{error.data && <JsonView value={error.data} style={mode === 'dark' ? vscodeTheme.dark : vscodeTheme.light} />}</Box>}
                {response && !error && (
                  <>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                      <Typography>Status: <span style={{ color: response.status < 300 ? theme.palette.success.main : theme.palette.error.main }}>{response.status} {response.statusText}</span></Typography>
                      <Typography>Time: <span style={{ color: theme.palette.primary.main }}>{response.time}ms</span></Typography>
                      <Typography>Size: <span style={{ color: theme.palette.primary.main }}>{(response.size / 1024).toFixed(2)} KB</span></Typography>
                    </Stack>
                    <Tabs value={responseTab} onChange={(e, v) => setResponseTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <Tab label="Body" />
                      <Tab label="Headers" />
                    </Tabs>
                    <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                      {responseTab === 0 && <JsonView value={response.data} style={mode === 'dark' ? vscodeTheme.dark : vscodeTheme.light} />}
                      {responseTab === 1 && <JsonView value={response.headers} style={mode === 'dark' ? vscodeTheme.dark : vscodeTheme.light} />}
                    </Box>
                  </>
                )}
              </Box>
            </Panel>
          </PanelGroup>
        </Box>
      </Panel>
    </PanelGroup>
    </Box>
    </ToolPageContainer>
    </>
  );
}

export default ApiClient;
